import { motion } from "framer-motion";
import {
    FileCheck,
    UserCheck,
    Leaf,
    Heart,
    Target,
    LineChart,
    Megaphone,
    LifeBuoy,
} from "lucide-react";

const LOGO_URL = "/communiti-logo.png";

const NODES = [
    { id: "quality",        label: "Quality Content Delivery",     Icon: FileCheck, x: 12, y: 18 },
    { id: "client",         label: "Client-Centric Approach",      Icon: UserCheck, x: 50, y: 8  },
    { id: "organic",        label: "Real & Organic Results",       Icon: Leaf,      x: 88, y: 18 },
    { id: "personalised",   label: "Personalised Brand Experiences", Icon: Heart,   x: 96, y: 50 },
    { id: "strategy",       label: "Strategy-Driven Execution",    Icon: Target,    x: 88, y: 82 },
    { id: "performance",    label: "Performance-Focused Mindset",  Icon: LineChart, x: 50, y: 92 },
    { id: "communication",  label: "Consistent Brand Communication", Icon: Megaphone, x: 4, y: 82 },
    { id: "support",        label: "End-to-End Support",           Icon: LifeBuoy,  x: 12, y: 50 },
];

const CENTER = { x: 50, y: 50 };

export default function Ecosystem() {
    return (
        <section
            id="ecosystem"
            data-testid="ecosystem-section"
            className="relative bg-white py-24 lg:py-32 overflow-hidden"
        >
            <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start mb-10 lg:mb-12">
                    <div className="lg:col-span-4 text-center md:text-left">
                        <p className="label-eyebrow">Our Ecosystem</p>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-communiti-text"
                        >
                            Why{" "}
                            <span className="font-serif-it text-communiti brand-name">
                                Commmmuniti?
                            </span>
                        </motion.h2>
                        <p className="mt-8 max-w-sm text-sm leading-relaxed text-communiti-muted">
                            At Commmmuniti, everything we create is interconnected. Each
                            practice fuels the next, forming a continuous loop of
                            creativity, strategy, and execution that leads to stronger,
                            compounding brand growth.
                        </p>
                    </div>

                    <div className="lg:col-span-8 w-full">
                        <div
                            className="relative mx-auto w-full max-w-[820px] scale-[0.85] sm:scale-90 md:scale-95 lg:scale-100"
                            style={{ aspectRatio: "5 / 4" }}
                        >
                            <svg
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                className="absolute inset-0 h-full w-full"
                                aria-hidden
                            >
                                {NODES.map((n, i) => (
                                    <motion.line
                                        key={n.id}
                                        x1={CENTER.x}
                                        y1={CENTER.y}
                                        x2={n.x}
                                        y2={n.y}
                                        stroke="#0F3D7A"
                                        strokeOpacity="0.25"
                                        strokeWidth="0.18"
                                        strokeDasharray="0.6 0.6"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{
                                            duration: 1.2,
                                            delay: 0.2 + i * 0.08,
                                            ease: "easeInOut",
                                        }}
                                    />
                                ))}
                            </svg>

                            {/* Center node */}
                            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                    className="relative h-32 w-32 sm:h-40 sm:w-40 md:h-44 md:w-44 lg:h-52 lg:w-52 flex items-center justify-center pointer-events-auto"
                                >
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                                        className="absolute inset-0 rounded-full border border-dashed border-communiti/30"
                                    />
                                    <motion.div
                                        animate={{ rotate: -360 }}
                                        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                        className="absolute inset-4 rounded-full border border-communiti/15"
                                    />
                                    <div
                                        data-testid="ecosystem-center"
                                        className="relative z-10 rounded-full bg-white h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(15,61,122,0.5)]"
                                    >
                                        <img
                                            src={LOGO_URL}
                                            alt="Commmmuniti Media Marketing"
                                            className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 lg:h-20 lg:w-20 object-contain"
                                        />
                                    </div>
                                </motion.div>
                            </div>

                            {/* Outer nodes */}
                            {NODES.map((n, i) => (
                                <motion.div
                                    key={n.id}
                                    data-testid={`ecosystem-node-${n.id}`}
                                    initial={{ opacity: 0, scale: 0.6, y: 10 }}
                                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.4 + i * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    style={{
                                        left: `${n.x}%`,
                                        top: `${n.y}%`,
                                        x: "-50%",
                                        y: "-50%",
                                    }}
                                    className="absolute"
                                >
                                    <div className="group inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-white border border-communiti-line px-2.5 py-1.5 sm:px-3.5 sm:py-2 tile-shadow hover:border-communiti hover:-translate-y-0.5 transition-all duration-300 max-w-[140px] sm:max-w-[180px] md:max-w-[200px]">
                                        <span className="inline-flex h-5 w-5 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-communiti/10 text-communiti shrink-0">
                                            <n.Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                        </span>
                                        <span className="text-[10px] sm:text-[12px] font-medium text-communiti-text leading-tight">
                                            {n.label}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
